import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { MDBAlert, MDBBadge, MDBBtn, MDBCard, MDBCardBody, MDBCol, MDBIcon, MDBRow, MDBSpinner } from 'mdb-react-ui-kit';
import { UserContext } from '../../../../App';
import { BOOTH_STATUS, boothKeyOf, buildCheckoutUrl, defaultFilters, holdRemainingSeconds } from '../../../../utils/boothMap';
import { getBoothAssetUrl, preloadBoothImage } from '../../../../utils/boothAssets';
import { getLocalizedText } from '../../../../utils/localizedText';
import { isAdminOrModerator } from '../../../../utils/roles';
import { captureError } from '../../../../utils/sentryHelpers';
import useEventBoothMap from './hooks/useEventBoothMap';
import BoothFilters from './BoothFilters';
import HallPlanMap from './HallPlanMap';
import BoothDetailsPanel from './BoothDetailsPanel';
import { formatDay, isOwnStatus } from './boothStatusText';
import { boothBookingWindow, filterOptionsOf, findBoothByKey, valueSpan } from './boothMapView';
import '../../../../components/booth-booking/booth-booking.scss';

const client = generateClient();

const GET_PRODUCT_PACKAGE = /* GraphQL */ `
  query GetProduct($id: ID!) {
    getProduct(id: $id) {
      id
      includes {
        language
        text
      }
      description {
        language
        text
      }
      components {
        items {
          qty
          order
          componentProduct {
            id
            name {
              language
              text
            }
          }
        }
      }
    }
  }
`;

const HIT_HIGHLIGHT_MS = 5000;
// pointer rest on a booth before its product image is preloaded (no requests while sweeping across the plan)
const IMAGE_PRELOAD_DELAY_MS = 150;

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('de-DE');
}

/**
 * Info text of a closed booking window (see boothBookingWindow; both window days are bookable,
 * so after the end the text names the last bookable day). Literal t() keys for Locize.
 */
function bookingWindowText(t, bookingWindow) {
  const date = formatDay(bookingWindow.date);
  if (bookingWindow.reason === 'not-started' && date) return t('Booth booking opens on {{date}}.', { date });
  if (bookingWindow.reason === 'ended' && date) return t('Booth booking was possible until {{date}}.', { date });
  return t('Booth booking is not open for this event.');
}

/**
 * Interactive hall-plan booth booking (test page next to the legacy SelectBooth table).
 * Route: /mycompany/:companyid/select-booth-map/:eventId
 */
export default function SelectBoothMap() {
  const { t } = useTranslation();
  const { companyid, eventId } = useParams();
  const { user } = useContext(UserContext);

  const isMember = !!(user?.employeeProfiles?.items || []).some(
    (profile) => profile && profile.company && profile.company.id === companyid && profile.status === 'active'
  );
  const hasAccess = isMember || isAdminOrModerator(user);
  // UserContext merges the id token with the DB user, which arrives later (only the DB user has an id):
  // until then employeeProfiles are missing and a member would wrongly be denied.
  const userLoaded = !!(user && user.id);

  const { map, booths, loading, error, refresh, actions, busyId, notice, clearNotice, clockOffsetMs } = useEventBoothMap({
    eventId,
    companyId: companyid,
    enabled: hasAccess,
  });

  const [filters, setFilters] = useState(defaultFilters);
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [hitId, setHitId] = useState(null);
  const [focusKey, setFocusKey] = useState(0);
  const [productDetails, setProductDetails] = useState({});
  const [planImage, setPlanImage] = useState({ s3Path: '', url: '', failed: false });
  const [nowMs, setNowMs] = useState(() => Date.now());

  const requestedProductsRef = useRef(new Set());
  const expiredHandledRef = useRef(new Set());

  const event = map && map.event ? map.event : null;
  const plan = event && event.boothPlan ? event.boothPlan : null;
  const planS3Path = plan && plan.image && plan.image.s3Path ? plan.image.s3Path : '';
  // Switch + status + optional window (boothBookingStart/End); the Lambda refuses with EVENT_NOT_BOOKABLE anyway.
  // Evaluated on every render (polling re-renders at least every 20 s) against the server clock.
  const bookingWindow = boothBookingWindow(event, Date.now() + clockOffsetMs);
  const bookingOpen = bookingWindow.open;

  // ----- plan image URL (memoised per s3Path) ----------------------------------------------
  useEffect(() => {
    if (!planS3Path || planImage.s3Path === planS3Path) return undefined;
    let cancelled = false;
    getBoothAssetUrl(planS3Path)
      .then((url) => {
        if (!cancelled) setPlanImage({ s3Path: planS3Path, url: url || '', failed: !url });
      })
      .catch((err) => {
        captureError(err, {
          feature: 'booth-booking',
          operation: 'load-booth-plan-image',
          consoleMessage: 'Loading the hall plan image URL failed',
          context: { eventId, s3Path: planS3Path },
        });
        if (!cancelled) setPlanImage({ s3Path: planS3Path, url: '', failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [planS3Path, planImage.s3Path, eventId]);
  const planUrl = planImage.s3Path === planS3Path ? planImage.url : '';
  const planUrlFailed = !!planS3Path && planImage.s3Path === planS3Path && planImage.failed;

  // ----- derived booth data ------------------------------------------------------------------
  const selectedBooth = useMemo(() => findBoothByKey(booths, selectedId), [booths, selectedId]);
  const heldBooths = useMemo(() => booths.filter((b) => isOwnStatus(b.status)), [booths]);
  const hasPlacedBooths = useMemo(() => booths.some((b) => !!b.points), [booths]);

  // slider spans over the bookable booths (all booths when none is left): the real min/max, not rounded
  const { priceSpan, areaSpan } = useMemo(() => {
    const open = booths.filter((b) => b.status !== BOOTH_STATUS.SOLD && b.status !== BOOTH_STATUS.UNAVAILABLE);
    const source = open.length > 0 ? open : booths;
    return { priceSpan: valueSpan(source, 'price'), areaSpan: valueSpan(source, 'area') };
  }, [booths]);

  // chip groups of the filter bar (position type / zone / offer), only for dimensions with several values
  const filterOptions = useMemo(() => filterOptionsOf(map), [map]);
  // a chip group that disappeared (e.g. after a poll) must not keep filtering invisibly
  const mapFilters = useMemo(
    () => ({
      ...filters,
      positionTypes: filterOptions.positionTypes.length > 1 ? filters.positionTypes || [] : [],
      zones: filterOptions.zones.length > 1 ? filters.zones || [] : [],
      offerCodes: filterOptions.offerCodes.length > 1 ? filters.offerCodes || [] : [],
    }),
    [filters, filterOptions]
  );

  const checkoutUrlFor = useCallback(
    (own) =>
      buildCheckoutUrl({
        items: own.map((b) => ({ productId: b.productId, boothNumber: b.boothNumber })),
        companyId: companyid,
        eventId,
        notes: 'Hallenplan: ' + own.map((b) => 'Stand ' + b.boothNumber).join(', '),
      }),
    [companyid, eventId]
  );
  const checkoutUrl = useMemo(() => checkoutUrlFor(heldBooths), [checkoutUrlFor, heldBooths]);

  // "Book booth": hold the booth, then straight to the checkout with all own booths (a failed hold keeps the
  // page and shows the notice of the action)
  const navigate = useNavigate();
  const bookBooth = useCallback(
    async (booth) => {
      const outcome = await actions.reserve(booth);
      if (!outcome || !outcome.success) return;
      const key = boothKeyOf(booth);
      clearNotice();
      navigate(checkoutUrlFor([...heldBooths.filter((b) => boothKeyOf(b) !== key), booth]));
    },
    [actions, heldBooths, checkoutUrlFor, navigate, clearNotice]
  );

  // ----- product image ahead of the click ----------------------------------------------------
  // while the pointer rests on a booth, its product image loads into the browser cache (once per file); 3D models
  // stay on demand (8-19 MB each)
  const hoveredImage = useMemo(() => {
    const booth = hoveredId ? findBoothByKey(booths, hoveredId) : null;
    return booth && booth.image && booth.image.fileName ? booth.image.fileName : '';
  }, [booths, hoveredId]);

  useEffect(() => {
    if (!hoveredImage) return undefined;
    const timer = setTimeout(() => preloadBoothImage(hoveredImage), IMAGE_PRELOAD_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hoveredImage]);

  // ----- countdown clock + refresh when an own hold runs out ---------------------------------
  const runningHolds = useMemo(
    () => booths.filter((b) => b.expiresAt && (b.status === BOOTH_STATUS.HELD_BY_YOU || boothKeyOf(b) === selectedId)),
    [booths, selectedId]
  );

  useEffect(() => {
    if (runningHolds.length === 0) return undefined;
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [runningHolds.length]);

  const serverNowMs = nowMs + clockOffsetMs;

  useEffect(() => {
    for (const booth of runningHolds) {
      const key = `${boothKeyOf(booth)}:${booth.expiresAt}`;
      if (holdRemainingSeconds(booth.expiresAt, serverNowMs) === 0 && !expiredHandledRef.current.has(key)) {
        expiredHandledRef.current.add(key);
        refresh();
        break;
      }
    }
  }, [runningHolds, serverNowMs, refresh]);

  // ----- included package of the selected booth (once per productId) -------------------------
  // Depends on the productId, not on selectedBooth: that object is re-created on every poll.
  const selectedProductId = selectedBooth ? selectedBooth.productId : null;
  useEffect(() => {
    const productId = selectedProductId;
    if (!productId || requestedProductsRef.current.has(productId)) return;
    requestedProductsRef.current.add(productId);
    setProductDetails((prev) => ({ ...prev, [productId]: { loading: true, product: null, error: null } }));
    client
      .graphql({ query: GET_PRODUCT_PACKAGE, variables: { id: productId } })
      .then((result) => {
        const product = result && result.data ? result.data.getProduct : null;
        setProductDetails((prev) => ({ ...prev, [productId]: { loading: false, product, error: null } }));
      })
      .catch((err) => {
        captureError(err, {
          feature: 'booth-booking',
          operation: 'fetch-booth-product-package',
          consoleMessage: 'Loading the booth package failed',
          context: { productId, eventId },
        });
        // selecting the booth again (after another or none) retries; polls do not
        requestedProductsRef.current.delete(productId);
        setProductDetails((prev) => ({ ...prev, [productId]: { loading: false, product: null, error: err } }));
      });
  }, [selectedProductId, eventId]);

  // ----- search hit highlight -----------------------------------------------------------------
  useEffect(() => {
    if (!hitId) return undefined;
    const timeout = setTimeout(() => setHitId(null), HIT_HIGHLIGHT_MS);
    return () => clearTimeout(timeout);
  }, [hitId]);

  const selectBooth = useCallback((booth) => {
    setSelectedId(booth ? boothKeyOf(booth) : null);
  }, []);

  const pickSearchHit = useCallback((booth) => {
    if (!booth) return;
    const key = boothKeyOf(booth);
    setSelectedId(key);
    setHitId(key);
    setFocusKey((k) => k + 1);
    setFilters((prev) => ({ ...prev, query: '' }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters());
    setHitId(null);
  }, []);

  // ----- render -------------------------------------------------------------------------------
  if (!hasAccess && !userLoaded) {
    return (
      <div className="d-flex justify-content-center py-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">{t('Loading...')}</span>
        </MDBSpinner>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="px-3 px-lg-4 py-4">
        <MDBAlert open color="danger">
          {t('You are not a member of this company.')}
        </MDBAlert>
      </div>
    );
  }

  if (loading && !map) {
    return (
      <div className="d-flex justify-content-center py-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">{t('Loading...')}</span>
        </MDBSpinner>
      </div>
    );
  }

  if (!map) {
    return (
      <div className="px-3 px-lg-4 py-4">
        <MDBAlert open color="danger">
          <div>{t('The hall plan could not be loaded.')}</div>
          {error && error.message ? <div className="small mt-1">{error.message}</div> : null}
          <MDBBtn size="sm" color="danger" className="mt-2" onClick={refresh}>
            {t('Try again')}
          </MDBBtn>
        </MDBAlert>
        <Link to={`/mycompany/${companyid}/select-booth/${eventId}`}>{t('Switch to list view')}</Link>
      </div>
    );
  }

  const eventName = getLocalizedText(event && event.name) || '';
  const startDate = formatDate(event && event.startDate);
  const endDate = formatDate(event && event.endDate);
  const locationName = getLocalizedText(event && event.location && event.location.name) || '';
  const city = (event && event.location && event.location.address && event.location.address.city) || '';
  const bookingNotice = getLocalizedText(event && event.bookingNotice);

  // Same steps as the checkout this page leads to (Checkout.jsx: Your information · Payment · Confirmation).
  const steps = [t('Select booth'), t('Your information'), t('Payment'), t('Confirmation')];

  return (
    <div className="px-2 px-md-3 px-xl-4 py-3">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <div className="text-uppercase small fw-bold text-primary mb-1">{t('Booth booking')}</div>
          <h1 className="h3 fw-bold mb-1">{t('Select a booth for {{event}}', { event: eventName })}</h1>
          <div className="text-muted small">
            {startDate && (
              <span className="me-3">
                <MDBIcon far icon="calendar" className="me-1" />
                {startDate}
                {endDate && endDate !== startDate ? ` – ${endDate}` : ''}
              </span>
            )}
            {(locationName || city) && (
              <span>
                <MDBIcon fas icon="map-marker-alt" className="me-1" />
                {[locationName, city].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <div className="d-flex flex-wrap gap-2">
            {steps.map((label, index) => (
              <MDBBadge key={label} pill color={index === 0 ? 'primary' : 'light'} className={index === 0 ? 'px-3 py-2' : 'px-3 py-2 text-muted'}>
                {index + 1} · {label}
              </MDBBadge>
            ))}
          </div>
          {/* the list is only an alternative while no booth is placed on the hall plan yet */}
          {hasPlacedBooths ? null : (
            <Link to={`/mycompany/${companyid}/select-booth/${eventId}`} className="small">
              <MDBIcon fas icon="list" className="me-1" />
              {t('Switch to list view')}
            </Link>
          )}
        </div>
      </div>

      {!bookingOpen && (
        <MDBAlert open color="light" className="mb-3 text-primary border border-primary" data-testid="booth-booking-window">
          <MDBIcon fas icon="info-circle" className="me-2" />
          {bookingWindowText(t, bookingWindow)}
        </MDBAlert>
      )}
      {bookingNotice ? (
        <MDBAlert open color="light" className="mb-3 small text-primary border border-primary" style={{ whiteSpace: 'pre-line' }}>
          {bookingNotice}
        </MDBAlert>
      ) : null}
      {error ? (
        <div className="small text-warning mb-2">
          <MDBIcon fas icon="exclamation-triangle" className="me-1" />
          {t('The hall plan could not be updated. The displayed availability may be outdated.')}
        </div>
      ) : null}

      <MDBRow className="g-3">
        <MDBCol lg="9">
          <MDBCard>
            <MDBCardBody className="p-2 p-md-3 border-bottom">
              <BoothFilters
                booths={booths}
                filters={filters}
                onChange={setFilters}
                onReset={resetFilters}
                priceRange={priceSpan}
                areaRange={areaSpan}
                filterOptions={filterOptions}
                onPickSearchHit={pickSearchHit}
                bookingOpen={bookingOpen}
              />
            </MDBCardBody>
            <MDBCardBody className="p-2 p-md-3">
              <HallPlanMap
                plan={plan}
                booths={booths}
                unassignedAreas={map.unassignedAreas}
                selectedId={selectedId}
                hoveredId={hoveredId}
                hitId={hitId}
                focusKey={focusKey}
                filters={mapFilters}
                onSelect={selectBooth}
                onHover={setHoveredId}
                planUrl={planUrl}
                planUrlFailed={planUrlFailed}
                bookingOpen={bookingOpen}
              />
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
        <MDBCol lg="3">
          <BoothDetailsPanel
            booth={selectedBooth}
            counts={map.counts}
            variants={map.variants}
            products={map.products}
            productDetails={selectedBooth ? productDetails[selectedBooth.productId] : null}
            onClear={() => setSelectedId(null)}
            onSelectBooth={selectBooth}
            onBook={bookBooth}
            onRelease={actions.release}
            heldBooths={heldBooths}
            checkoutUrl={checkoutUrl}
            busy={!!busyId}
            bookingOpen={bookingOpen}
            bookingWindow={bookingWindow}
            nowMs={serverNowMs}
            notice={notice}
            onDismissNotice={clearNotice}
          />
        </MDBCol>
      </MDBRow>
    </div>
  );
}
